import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { getCountryCallingCode, type CountryCode } from "libphonenumber-js";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";
import {
  countryOptions,
  defaultCountryForLocale,
  resolvePageLocale,
  toE164,
} from "@/lib/phone";

/**
 * Sigla do país virando bandeira: as duas letras viram os "regional indicator
 * symbols" do Unicode. No Windows o Chrome não desenha bandeira e mostra as
 * duas letras — o que continua informativo, só não é bonito.
 */
const flagEmoji = (code: string): string =>
  code
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));

interface PhoneFieldProps {
  id?: string;
  name?: string;
  /** Valor em E.164, ou "" quando vazio ou inválido. Passar "" reseta o campo. */
  value: string;
  /** Recebe o E.164 já normalizado, ou "" se o que está digitado não disca. */
  onChange: (e164: string) => void;
  required?: boolean;
  placeholder?: string;
  /** Classes do input do formulário. O seletor de país recebe as mesmas, para combinar. */
  className?: string;
  disabled?: boolean;
}

/**
 * Campo de telefone com seletor de país.
 *
 * O componente guarda o que a pessoa digitou e devolve para o formulário o
 * número já em E.164 — ou "" quando o que está ali não daria para discar.
 * Assim o CRM nunca recebe número montado errado, e nenhum formulário precisa
 * saber normalizar nada.
 *
 * O país vem pré-selecionado pelo idioma da página, o que resolve a maioria
 * dos casos sem a pessoa tocar no seletor.
 */
const PhoneField = ({
  id,
  name = "phone",
  value,
  onChange,
  required,
  placeholder,
  className,
  disabled,
}: PhoneFieldProps) => {
  const { t, language } = useLanguage();
  const locale = resolvePageLocale();

  const [country, setCountry] = useState<CountryCode>(() =>
    defaultCountryForLocale(locale)
  );
  const [raw, setRaw] = useState("");
  const [touched, setTouched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const options = useMemo(
    () => countryOptions(language.toLowerCase()),
    [language]
  );

  const selectedCallingCode = useMemo(() => {
    try {
      return getCountryCallingCode(country);
    } catch {
      return "";
    }
  }, [country]);

  // O formulário limpa os campos depois do envio; acompanhamos esse reset.
  //
  // A comparação com o que este campo emitiria é o que separa um reset de
  // verdade do caminho normal de digitação: apagar um dígito até o número
  // ficar incompleto também faz o pai voltar para "", e sem essa checagem
  // o campo se apagava inteiro no meio da correção.
  //
  // Depende só de `value` de propósito. O efeito roda no render em que
  // `value` mudou, que é o mesmo em que `raw` já está atualizado.
  useEffect(() => {
    if (value === "" && toE164(raw.trim(), country) !== "") {
      setRaw("");
      setTouched(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const trimmed = raw.trim();
  const isInvalid = trimmed !== "" && toE164(trimmed, country) === "";

  // Bloqueia o envio nativo quando o número não disca, sem que cada
  // formulário precise validar por conta própria.
  useEffect(() => {
    inputRef.current?.setCustomValidity(isInvalid ? t("phone.invalid") : "");
  }, [isInvalid, t]);

  const emit = (nextRaw: string, nextCountry: CountryCode) => {
    onChange(toE164(nextRaw.trim(), nextCountry));
  };

  const handleCountry = (next: CountryCode) => {
    setCountry(next);
    emit(raw, next);
  };

  const handleRaw = (next: string) => {
    setRaw(next);
    emit(next, country);
  };

  return (
    <div>
      <div className="flex gap-2">
        {/*
          O <select> de verdade fica invisível por cima do bloco bonito.
          Assim o visual é nosso, mas quem abre a lista é o navegador — o que
          no celular dá a roleta nativa do sistema, bem melhor que qualquer
          dropdown que a gente desenhasse.
        */}
        <div
          className={cn(
            className,
            "relative flex w-[5.75rem] shrink-0 items-center gap-1.5 px-3"
          )}
        >
          <span aria-hidden className="text-base leading-none">
            {flagEmoji(country)}
          </span>
          <span className="text-sm font-medium tabular-nums">
            +{selectedCallingCode}
          </span>
          <ChevronDown aria-hidden className="ml-auto h-3.5 w-3.5 opacity-50" />
          <select
            aria-label={t("phone.country")}
            value={country}
            disabled={disabled}
            onChange={(e) => handleCountry(e.target.value as CountryCode)}
            className="absolute inset-0 h-full w-full cursor-pointer appearance-none opacity-0"
          >
            {options.map((option) => (
              <option key={option.code} value={option.code}>
                {flagEmoji(option.code)} {option.name} +{option.callingCode}
              </option>
            ))}
          </select>
        </div>

        <input
          ref={inputRef}
          type="tel"
          id={id}
          name={name}
          inputMode="tel"
          autoComplete="tel-national"
          value={raw}
          disabled={disabled}
          required={required}
          placeholder={placeholder}
          onChange={(e) => handleRaw(e.target.value)}
          onBlur={() => setTouched(true)}
          aria-invalid={touched && isInvalid}
          className={cn(className, "min-w-0 flex-1")}
        />
      </div>

      {touched && isInvalid && (
        <p className="mt-1 text-sm text-red-600" role="alert">
          {t("phone.invalid")}
        </p>
      )}
    </div>
  );
};

export default PhoneField;
